import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import {
  useSearchParams,
  useLocation as useRouterLocation,
  useNavigate,
} from "react-router-dom";
import {
  useGetActiveServiceAreas,
  ServiceAreaItem,
} from "../api-data/service-provider/service-areas";
import {
  useSearchServiceAreasLocality,
  ServiceAreaLocalityItem,
  ServiceAreaLocalityApiResponse,
} from "../api-data/service-provider/service-area-locality";
import LocationModal from "../new-pages/home-page/LocationModal";
import { useTrackEvent } from "../hooks/useTrackEvent";
import { EventType } from "../api-data/reporting/enums";
import {
  buildSearchUrl,
  parseSearchParams as parseUrlParams,
  toSlug,
} from "../utils/urlHelpers";
import { useAuth } from "./AuthContext";

const STORAGE_KEY_CITY = "dragonfly_selected_city";
const STORAGE_KEY_LOCALITY = "dragonfly_selected_locality";

interface LocationContextType {
  selectedCity: string;
  selectedLocality: string;
  /** Translated city name for display (falls back to English) */
  displayCity: string;
  /** Translated locality name for display (falls back to English) */
  displayLocality: string;
  selectedCityId?: number;
  selectedLocalityId?: number;
  cities: ServiceAreaItem[];
  citiesLoading: boolean;
  setLocation: (
    city: string,
    locality?: string,
    cityId?: number,
    localityId?: number,
  ) => void;
  clearLocation: () => void;
  openLocationDialog: (source?: string) => void;
  closeLocationDialog: () => void;
  isLocationDialogOpen: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();
  const { data: serviceAreasResponse, isLoading: citiesLoading } =
    useGetActiveServiceAreas("city,asc");

  const cities = useMemo(
    () => serviceAreasResponse?.data || [],
    [serviceAreasResponse],
  );

  const [selectedCity, setSelectedCity] = useState<string>("");
  const [selectedLocality, setSelectedLocality] = useState<string>("");
  const [selectedCityId, setSelectedCityId] = useState<number | undefined>(
    undefined,
  );
  const [selectedLocalityId, setSelectedLocalityId] = useState<
    number | undefined
  >(undefined);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [dialogSource, setDialogSource] = useState<string | undefined>(
    undefined,
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [isUpdatingFromContext, setIsUpdatingFromContext] = useState(false);

  const { trackEvent } = useTrackEvent();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  // Fetch localities for the selected city to resolve translated names
  const selectedCityObj = useMemo(
    () => cities.find((c) => c.city === selectedCity),
    [cities, selectedCity],
  );

  const { data: localityResponse } = useSearchServiceAreasLocality(
    100,
    0,
    selectedCityObj?.hasLocality ? selectedCityObj.id : undefined,
    "locality,asc",
  );

  const localities = useMemo<ServiceAreaLocalityItem[]>(
    () =>
      localityResponse?.pages?.flatMap(
        (p: ServiceAreaLocalityApiResponse) => p.data,
      ) || [],
    [localityResponse],
  );

  // Translated display names — English value stays for API/URL usage
  const displayCity = useMemo(
    () => selectedCityObj?.localized?.city ?? selectedCity,
    [selectedCityObj, selectedCity],
  );

  const displayLocality = useMemo(() => {
    if (!selectedLocality) return "";
    const localityObj = localities.find((l) => l.locality === selectedLocality);
    return localityObj?.localized?.locality ?? selectedLocality;
  }, [selectedLocality, localities]);

  // Initial setup: URL params > localStorage > Auth Profile City > Default
  useEffect(() => {
    if (isInitialized || cities.length === 0) return;

    const cityFromURL = searchParams.get("city");
    const localityFromURL = searchParams.get("locality");
    const cityFromStorage = localStorage.getItem(STORAGE_KEY_CITY);
    const localityFromStorage = localStorage.getItem(STORAGE_KEY_LOCALITY);

    // Priority 1: URL parameters
    if (cityFromURL) {
      const cityObj = cities.find((c) => c.city === cityFromURL);
      setSelectedCity(cityFromURL);
      setSelectedCityId(cityObj?.id);
      setSelectedLocality(localityFromURL || "");
      setSelectedLocalityId(undefined); // locality ID not available from URL
      localStorage.setItem(STORAGE_KEY_CITY, cityFromURL);
      if (localityFromURL) {
        localStorage.setItem(STORAGE_KEY_LOCALITY, localityFromURL);
      } else {
        localStorage.removeItem(STORAGE_KEY_LOCALITY);
      }
      setIsInitialized(true);
    }
    // Priority 2: localStorage (always takes precedence over user profile)
    else if (cityFromStorage) {
      const cityObj = cities.find((c) => c.city === cityFromStorage);
      setSelectedCity(cityFromStorage);
      setSelectedCityId(cityObj?.id);
      setSelectedLocality(localityFromStorage || "");
      setSelectedLocalityId(undefined); // locality ID not available from storage
      setIsInitialized(true);
    }
    // Priority 3 & 4: Check authenticated user's city or use first city
    else {
      // Wait for auth to complete before deciding
      if (authLoading) return;

      let cityInitialized = false;

      // Priority 3: Use authenticated user's city if available
      if (isAuthenticated && user?.city) {
        const cityObj = cities.find((c) => c.city === user.city);
        if (cityObj) {
          setSelectedCity(user.city);
          setSelectedCityId(cityObj.id);
          const finalLocality = cityObj.hasLocality ? (user.locality || "") : "";
          setSelectedLocality(finalLocality);
          setSelectedLocalityId(undefined);
          // Save to localStorage for future visits
          localStorage.setItem(STORAGE_KEY_CITY, user.city);
          if (finalLocality) {
            localStorage.setItem(STORAGE_KEY_LOCALITY, finalLocality);
          } else {
            localStorage.removeItem(STORAGE_KEY_LOCALITY);
          }
          cityInitialized = true;
        }
      }

      // Priority 4: Fallback to first city
      if (!cityInitialized) {
        const firstCity = cities[0]?.city || "";
        if (firstCity) {
          setSelectedCity(firstCity);
          setSelectedCityId(cities[0]?.id);
          setSelectedLocality("");
          setSelectedLocalityId(undefined);
          localStorage.setItem(STORAGE_KEY_CITY, firstCity);
          localStorage.removeItem(STORAGE_KEY_LOCALITY);
        }
      }

      setIsInitialized(true);
    }
  }, [
    cities,
    searchParams,
    isInitialized,
    authLoading,
    isAuthenticated,
    user,
  ]);

  // Sync context state when URL params change (after initialization)
  useEffect(() => {
    if (!isInitialized || isUpdatingFromContext) return;

    const cityFromURL = searchParams.get("city");
    const localityFromURL = searchParams.get("locality");

    // Only update if URL params exist and differ from current state
    if (
      cityFromURL &&
      (cityFromURL !== selectedCity ||
        (localityFromURL || "") !== selectedLocality)
    ) {
      const cityObj = cities.find((c) => c.city === cityFromURL);
      setSelectedCity(cityFromURL);
      setSelectedCityId(cityObj?.id);
      setSelectedLocality(localityFromURL || "");
      setSelectedLocalityId(undefined);
      localStorage.setItem(STORAGE_KEY_CITY, cityFromURL);
      if (localityFromURL) {
        localStorage.setItem(STORAGE_KEY_LOCALITY, localityFromURL);
      } else {
        localStorage.removeItem(STORAGE_KEY_LOCALITY);
      }
    }
  }, [
    searchParams,
    isInitialized,
    isUpdatingFromContext,
    selectedCity,
    selectedLocality,
    cities,
  ]);

  const setLocation = (
    city: string,
    locality: string = "",
    cityId?: number,
    localityId?: number,
  ) => {
    setSelectedCity(city);
    const resolvedCityId = cityId ?? cities.find((c) => c.city === city)?.id;
    setSelectedCityId(resolvedCityId);

    const selectedCityObj = cities.find((c) => c.city === city);
    const finalLocality =
      selectedCityObj && !selectedCityObj.hasLocality ? "" : locality;

    if (selectedCityObj && !selectedCityObj.hasLocality) {
      setSelectedLocality("");
      setSelectedLocalityId(undefined);
      localStorage.setItem(STORAGE_KEY_CITY, city);
      localStorage.removeItem(STORAGE_KEY_LOCALITY);
    } else {
      setSelectedLocality(locality);
      setSelectedLocalityId(localityId);
      localStorage.setItem(STORAGE_KEY_CITY, city);
      if (locality) {
        localStorage.setItem(STORAGE_KEY_LOCALITY, locality);
      } else {
        localStorage.removeItem(STORAGE_KEY_LOCALITY);
      }
    }

    // Check if we're on the search page: /{city}, /{city}-{locality}, or /{keyword}-in-{city}...
    // Search pages are always a single path segment.
    const pathParts = routerLocation.pathname.split("/").filter(Boolean);
    const isSearchPage =
      pathParts.length === 1 &&
      cities.some((c) => {
        const slug = toSlug(c.city);
        return (
          pathParts[0] === slug || 
          pathParts[0].startsWith(`${slug}-`) || 
          pathParts[0].includes(`-in-${slug}`)
        );
      });

    if (isSearchPage) {
      // Parse the current slug to extract the keyword (if any)
      const urlParams = parseUrlParams(
        { location: pathParts[0] },
        cities,
      );

      // Navigate to new URL with updated location, preserving keyword
      setIsUpdatingFromContext(true);
      const newSearchUrl = buildSearchUrl(
        city,
        finalLocality,
        urlParams.keyword || undefined,
      );
      // Keep a category tap's categoryId (OOTWUI-006); typed searches have null state.
      navigate(newSearchUrl, { replace: true, state: routerLocation.state });
      setTimeout(() => setIsUpdatingFromContext(false), 0);
    } else {
      // Update URL params if they exist (for other pages)
      const hasCityParam = searchParams.has("city");
      const hasLocalityParam = searchParams.has("locality");

      if (hasCityParam || hasLocalityParam) {
        setIsUpdatingFromContext(true);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("city", city);
            if (finalLocality) {
              next.set("locality", finalLocality);
            } else {
              next.delete("locality");
            }
            return next;
          },
          { replace: true },
        );
        // Reset flag after URL update completes
        setTimeout(() => setIsUpdatingFromContext(false), 0);
      }
    }
  };

  const clearLocation = () => {
    const firstCity = cities[0]?.city || "";
    setSelectedCity(firstCity);
    setSelectedLocality("");
    if (firstCity) {
      localStorage.setItem(STORAGE_KEY_CITY, firstCity);
    }
    localStorage.removeItem(STORAGE_KEY_LOCALITY);
  };

  const openLocationDialog = (source?: string) => {
    setDialogSource(source);
    setIsLocationDialogOpen(true);
  };

  const closeLocationDialog = () => {
    setIsLocationDialogOpen(false);
    setDialogSource(undefined);
  };

  const handleLocationApply = (
    city: string,
    locality: string,
    cityId?: number,
    localityId?: number,
  ) => {
    setLocation(city, locality, cityId, localityId);
    console.log("Location applied:", city, locality, cityId, localityId);

    // Track CITY_SELECTED event only when user clicks Apply button
    trackEvent(EventType.CITY_SELECTED, {
      cityId: cityId ?? cities.find((c) => c.city === city)?.id,
      localityId: localityId,
      source: dialogSource,
    });

    closeLocationDialog();
  };

  return (
    <LocationContext.Provider
      value={{
        selectedCity,
        selectedLocality,
        displayCity,
        displayLocality,
        selectedCityId,
        selectedLocalityId,
        cities,
        citiesLoading,
        setLocation,
        clearLocation,
        openLocationDialog,
        closeLocationDialog,
        isLocationDialogOpen,
      }}
    >
      {children}
      <LocationModal
        open={isLocationDialogOpen}
        onClose={closeLocationDialog}
        onApply={handleLocationApply}
        cities={cities}
        initialCity={selectedCity}
        initialLocality={selectedLocality}
      />
    </LocationContext.Provider>
  );
};
